<template>
    <v-form ref="form" v-model="valid" data-cy="description-excursion-form" class="pb-4">
        <v-row v-if="loading" dense>
            <v-sheet class="full-width d-flex justify-center py-2">
                <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
            </v-sheet>
        </v-row>
        <template v-else>
            <v-row dense class="mt-4 mb-4">
                <v-col cols="12" sm="6" md="2">
                    <LocaleSwitcher @change="changeActiveLangCode" />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="2" order="2" order-md="1">
                    <span class="text-h6 text--secondary">
                        {{ $t('general_info') }}
                    </span>
                </v-col>
                <v-col cols="12" md="8" order="3" order-md="2">
                    <TextFieldLocale
                        v-model="name"
                        :label="`${$t('name')}*`"
                        :active-lang-code="activeLangCode"
                        data-cy="name"
                        :rules="[requiredRule('name'), maxLength(255)]"
                        class="mb-2"
                    />
                    <v-autocomplete
                        :model-value="excursion.contentProviderId"
                        :items="contentProviderDictionary"
                        :label="$t('source_of_description')"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        color="primary"
                        data-cy="source-of-description"
                        disabled
                    />
                    <v-text-field
                        :model-value="t(`serviceType.${Activity.Excursion}`)"
                        :label="t('category')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="category"
                        disabled
                    />
                    <v-select
                        v-model="variant"
                        :items="categoryDictionary"
                        item-title="name"
                        :label="$t('product_variant')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="variant"
                        hide-details
                        disabled
                        return-object
                    >
                        <template v-slot:selection="{item}">
                            {{ item.raw.name[activeLangCode] }}
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" md="2" class="pl-4" order="1" order-md="4">
                    <v-row dense>
                        <div v-if="excursion.id" class="text-medium-emphasis ml-auto px-4">
                            {{ $t('id') }}: {{ excursion.id }}
                        </div>
                    </v-row>
                    <v-row dense class="px-4">
                        <v-switch
                            :model-value="excursion.active"
                            color="primary"
                            :label="excursion.active ? $t('active') : $t('inactive')"
                            data-cy="status"
                            hide-details
                            density="compact"
                            class="ml-auto pb-2"
                            disabled
                        />
                    </v-row>
                </v-col>
            </v-row>
            <v-row dense class="mt-4">
                <v-col cols="12" md="2">
                    <span class="text-h6 text--secondary">
                        {{ $t('start_end_points') }}
                    </span>
                </v-col>
                <v-col cols="12" md="8">
                    <v-row dense class="px-1 pb-8">
                        <v-col cols="12" md="6">
                            <div class="text-subtitle-1 font-weight-medium">{{ $t('starting_location') }}</div>
                            <InfoLocationForm v-model="excursion.pickUpPoints" :disabled="true" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <div class="text-subtitle-1 font-weight-medium">{{ $t('ending_location') }}</div>
                            <InfoLocationForm v-model="excursion.dropOffPoints" :disabled="true" />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>
        <v-row dense class="px-3">
            <v-btn color="primary" class="save-btn mr-4" data-cy="save" :disabled="loading || !valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                color="white"
                data-cy="cancel"
                :disabled="loading"
                :to="{name: 'supplierProducts', hash: '#activities'}"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {maxLength, requiredRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {clone} from '~/utils/helpers'
    import InfoLocationForm from '~/components/products/information/InfoLocationForm.vue'
    import type {Ref} from 'vue'
    import {Activity} from '~/utils/types/types'
    import {appInstance} from '~/utils/app-accessor'

    const app = useNuxtApp()
    const {t} = useI18n()

    const props = defineProps<{
        excursionInfo: any
        loading?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'updated'): void
    }>()

    const valid = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const excursion: Ref = ref({})
    const categoryDictionary = ref([])
    const contentProviderDictionary = ref([])
    const isLoading = ref<boolean>(false)

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    watch(
        () => props.excursionInfo,
        () => {
            excursion.value = clone(props.excursionInfo)
        },
        {immediate: true}
    )

    onMounted(async () => {
        await loadExcursionsCategoryDictionaryInfo()
        await loadExcursionsContentProviderDictionaryInfo()
    })

    async function loadExcursionsCategoryDictionaryInfo(): Promise<void> {
        try {
            categoryDictionary.value = await app.$api.excursionCategories.get()
        } catch (e) {
            categoryDictionary.value = []
        }
    }

    async function loadExcursionsContentProviderDictionaryInfo(): Promise<void> {
        try {
            contentProviderDictionary.value = (
                await appInstance.$api.contentProviders.get({product: Activity.Excursion})
            )
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter(({id}) => id !== 1)
        } catch (e) {
            contentProviderDictionary.value = []
        }
    }

    const name = computed({
        get: () => excursion.value?.name?.[activeLangCode.value] || '',
        set: value => {
            excursion.value.name[activeLangCode.value] = value
        },
    })

    const variant = computed({
        get: () => excursion.value?.category || null,
        set: value => (excursion.value.category = value || null),
    })

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(excursion.value)
            await app.$api.excursions.put(data)
            emit('updated')
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>

<style scoped lang="scss"></style>
